import React, { useState } from 'react';
import UserTasksDialog from './UserTasksDialog';
import { audienceColor, audienceLabel } from './audience';

const fmt = (n, decimals = 0) =>
  typeof n === 'number' ? n.toLocaleString('ru-RU', { maximumFractionDigits: decimals }) : '—';

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <span className="ml-1 text-paper-track">↕</span>;
  return <span className="ml-1 text-paper-ink">{sortOrder === 'desc' ? '↓' : '↑'}</span>;
};

const UsersTable = ({ data, loading, sortBy, sortOrder, onSort, page, totalPages, onPage }) => {
  const [selectedUser, setSelectedUser] = useState(null);

  const col = (field, label, tooltip) => (
    <th
      className="pb-2 pr-4 text-right cursor-pointer select-none hover:text-paper-ink"
      onClick={() => onSort(field)}
      title={tooltip}
    >
      {label}
      <SortIcon field={field} sortBy={sortBy} sortOrder={sortOrder} />
    </th>
  );

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-paper-line text-left font-mono text-[11px] uppercase tracking-[0.06em] text-paper-ink-3">
              <th className="py-3 pr-4" title="Email пользователя и количество API-ключей">Пользователь</th>
              <th className="py-3 pr-4" title="Офис: контрагент — из справочника (Админка → Офисы), сотрудник — из org-directory (GAL)">Офис / компания</th>
              <th className="py-3 pr-4" title="По роли учётки: сотрудник, клиент, партнёр или служебная учётка">Группа</th>
              {col('total_tasks', 'Задачи', 'Количество задач транскрибации за период')}
              {col('audio_seconds', 'Аудио, мин', 'Суммарная длительность загруженных аудиофайлов')}
              {col('transcribe_minutes', 'ASR, мин', 'Чистое время работы модели распознавания (без ожидания в очереди)')}
              {col('wall_minutes', 'Wall, мин', 'Полное время от загрузки до готовности результата (включая очередь)')}
              {col('symbols_count', 'Символы', 'Количество символов в результатах транскрибации')}
              {col('error_count', 'Ошибки', 'Количество задач завершившихся с ошибкой')}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="py-8 text-center text-paper-ink-3">Загружаем…</td>
              </tr>
            )}
            {!loading && (!data || data.length === 0) && (
              <tr>
                <td colSpan={9} className="py-8 text-center text-paper-ink-3">Нет данных</td>
              </tr>
            )}
            {!loading && data && data.map((user, idx) => (
              // Строка открывает задачи человека (решение владельца 14.10.2026:
              // вместо отдельной колонки с кнопкой «Задачи»).
              <tr
                key={user.email || idx}
                tabIndex={0}
                title="Открыть задачи"
                className="cursor-pointer border-b border-paper-line hover:bg-paper-panel focus:bg-paper-panel focus:outline-none"
                onClick={() => {
                  // Выделял адрес мышью, чтобы скопировать, — диалог не нужен.
                  if (String(window.getSelection ? window.getSelection() : '')) return;
                  setSelectedUser(user);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedUser(user);
                  }
                }}
              >
                <td className="py-[10px] pr-4">
                  <div className="font-medium text-paper-ink">{user.email || 'Нет email'}</div>
                  {user.name && (
                    <div className="text-[12px] text-paper-ink-3" title={[user.department, user.job_title].filter(Boolean).join(' · ')}>{user.name}</div>
                  )}
                  <div className="font-mono text-[11px] text-paper-hint">
                    {user.tokens_count || 0} {user.tokens_count === 1 ? 'ключ' : 'ключей'}
                  </div>
                </td>
                <td className="whitespace-nowrap py-[10px] pr-4 text-paper-ink-2">
                  {user.office || <span className="text-paper-hint">—</span>}
                </td>
                <td className="whitespace-nowrap py-[10px] pr-4 text-paper-ink-2">
                  <span className="inline-flex items-center gap-[6px]">
                    <span className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: audienceColor(user.audience) }} />
                    {audienceLabel(user.audience)}
                  </span>
                </td>
                <td className="py-[10px] pr-4 text-right font-mono text-[12px] text-paper-ink">{fmt(user.total_tasks)}</td>
                <td className="py-[10px] pr-4 text-right font-mono text-[12px] text-paper-ink">{fmt(user.audio_seconds / 60, 1)}</td>
                <td className="py-[10px] pr-4 text-right font-mono text-[12px] text-paper-ink">{fmt(user.transcribe_minutes, 1)}</td>
                <td className="py-[10px] pr-4 text-right font-mono text-[12px] text-paper-ink">{fmt(user.wall_minutes, 1)}</td>
                <td className="py-[10px] pr-4 text-right font-mono text-[12px] text-paper-ink">{fmt(user.symbols_count)}</td>
                <td className="py-[10px] pr-4 text-right font-mono text-[12px] text-paper-ink">
                  {user.error_count > 0
                    ? <span className="text-paper-accent">{fmt(user.error_count)}</span>
                    : <span className="text-paper-hint">0</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 pt-3 text-[13px] text-paper-ink-3">
          <span>Страница {page} из {totalPages}</span>
          <button
            type="button"
            className="rounded-chip bg-paper-bg px-3 py-1 text-paper-ink hover:bg-paper-panel disabled:text-paper-hint"
            onClick={() => onPage(page - 1)}
            disabled={page === 1 || loading}
          >
            ←
          </button>
          <button
            type="button"
            className="rounded-chip bg-paper-bg px-3 py-1 text-paper-ink hover:bg-paper-panel disabled:text-paper-hint"
            onClick={() => onPage(page + 1)}
            disabled={page === totalPages || loading}
          >
            →
          </button>
        </div>
      )}

      {selectedUser && (
        <UserTasksDialog
          email={selectedUser.email || ''}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default UsersTable;
